import { App, Spin } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import { MobileNotificationCard } from '@/components/mobile/MobileNotificationCard';
import { NOTIFICATION_TYPE } from '@/constants/notification';
import { ROUTES } from '@/constants/routes';
import { ApiError } from '@/services/api/client';
import * as notificationsApi from '@/services/api/notifications';
import type { NotificationListItem, NotificationType } from '@/types/notification';

type NotificationTab = 'all' | 'unread' | 'project' | 'task' | 'comment';

const TAB_ITEMS: { key: NotificationTab; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'unread', label: '未读' },
  { key: 'project', label: '项目' },
  { key: 'task', label: '任务' },
  { key: 'comment', label: '点评' },
];

function parseNotificationTab(value: string | null): NotificationTab {
  if (value === 'unread' || value === 'project' || value === 'task' || value === 'comment') {
    return value;
  }
  return 'all';
}

function queryForTab(tab: NotificationTab): {
  readStatus?: 'UNREAD';
  type?: NotificationType;
} {
  switch (tab) {
    case 'unread':
      return { readStatus: 'UNREAD' };
    case 'project':
      return { type: NOTIFICATION_TYPE.PROJECT };
    case 'task':
      return { type: NOTIFICATION_TYPE.TASK };
    case 'comment':
      return { type: NOTIFICATION_TYPE.COMMENT };
    default:
      return {};
  }
}

interface NotificationsMobilePageProps {
  variant: 'leader' | 'employee';
}

export function NotificationsMobilePage({ variant }: NotificationsMobilePageProps) {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = parseNotificationTab(searchParams.get('tab'));
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<NotificationListItem[]>([]);
  const [total, setTotal] = useState(0);

  const detailBase =
    variant === 'leader'
      ? ROUTES.mobile.leader.notifications
      : ROUTES.mobile.employee.notifications;

  const queryParams = useMemo(() => queryForTab(activeTab), [activeTab]);

  const switchTab = (tab: NotificationTab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (tab === 'all') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', tab);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const result = await notificationsApi.fetchNotifications({
        page: 1,
        pageSize: 100,
        ...queryParams,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载消息列表失败';
      message.error(text);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [message, queryParams]);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  const handleReadAll = async () => {
    try {
      await notificationsApi.markAllNotificationsRead();
      message.success('已全部标记为已读');
      await loadNotifications();
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '操作失败';
      message.error(text);
    }
  };

  return (
    <div className="mobile-workbench">
      <div className="mobile-segment-tabs mobile-segment-tabs--compact mobile-segment-tabs--scroll">
        {TAB_ITEMS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`mobile-segment-tabs__item${activeTab === tab.key ? ' mobile-segment-tabs__item--active' : ''}`}
            onClick={() => switchTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">消息</span>
          <span className="mobile-section__meta">共 {total} 条</span>
        </div>

        {loading ? (
          <div className="mobile-card" style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : items.length === 0 ? (
          <MobileEmpty message="暂无消息" />
        ) : (
          items.map((item) => (
            <MobileNotificationCard
              key={item.receiverId}
              item={item}
              onClick={() => navigate(`${detailBase}/${item.receiverId}`)}
            />
          ))
        )}
      </section>

      {items.some((item) => item.readStatus === 'UNREAD') ? (
        <button type="button" className="mobile-text-btn" onClick={() => void handleReadAll()}>
          全部已读
        </button>
      ) : null}
    </div>
  );
}
